import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link as RLink } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FloppyDisk, PaperPlaneTilt, ArrowLeft, Warning, Lock,
  ChatCircle, GitDiff, CheckCircle, XCircle, ArrowUUpLeft,
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  TextHOne, TextHTwo, TextHThree, TrashSimple, Lightning,
} from "@phosphor-icons/react";

function ToolbarBtn({ active, onClick, disabled, children, ...rest }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} /* keep editor selection */
      onClick={onClick}
      disabled={disabled}
      className={`px-2 py-1 text-xs border rounded-sm transition-colors ${
        active ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
      } disabled:opacity-40`}
      {...rest}
    >
      {children}
    </button>
  );
}

function Banner({ status }) {
  const s = (status || "draft").toLowerCase();
  const cfg = {
    draft:       { text: "UNCONTROLLED · DRAFT", cls: "bg-amber-500 text-white" },
    in_review:   { text: "UNCONTROLLED · IN REVIEW", cls: "bg-blue-600 text-white" },
    in_approval: { text: "UNCONTROLLED · IN APPROVAL", cls: "bg-purple-600 text-white" },
    rejected:    { text: "UNCONTROLLED · SENT BACK", cls: "bg-red-600 text-white" },
    published:   { text: "CONTROLLED COPY", cls: "bg-emerald-600 text-white" },
  }[s] || { text: `STATUS: ${s.toUpperCase()}`, cls: "bg-slate-700 text-white" };
  return (
    <div className={`px-3 py-1.5 text-xs font-mono font-bold tracking-wider ${cfg.cls}`} data-testid="status-banner">
      ● {cfg.text}
    </div>
  );
}

export default function DocumentEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [redlineMode, setRedlineMode] = useState(false);
  const [diffHtml, setDiffHtml] = useState("");
  const [basePublishedHtml, setBasePublishedHtml] = useState("");
  const [newComment, setNewComment] = useState("");
  const [decisionComment, setDecisionComment] = useState("");

  const editorRef = useRef(null);          // <div contenteditable>
  const hydratedRef = useRef(false);       // ensures we only innerHTML= once per session
  const autosaveTimer = useRef(null);

  const isAuthor = session?.started_by_id === user?.id;
  const canDiscard = (isAuthor || user?.role === "admin") &&
                     (session?.status === "draft" || session?.status === "rejected");
  const isReviewer = user?.role === "manager" || user?.role === "admin";
  const isApprover = user?.role === "admin";
  const status = session?.status || "draft";
  const editable = status === "draft" || status === "rejected";

  // ---- HTML hydration (contenteditable, no React re-render inside) ----
  const hydrate = useCallback((html) => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML === html) return;
    editorRef.current.innerHTML = html || "";
    hydratedRef.current = true;
    setDirty(false);
  }, []);

  const currentHtml = () => editorRef.current?.innerHTML || "";

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/documents/${id}`),
        api.get(`/documents/${id}/draft`),
      ]);
      setDoc(d.data);
      const sess = s.data && Object.keys(s.data).length ? s.data : null;
      setSession(sess);
      if (sess?.html) hydrate(sess.html);
    } finally {
      setLoading(false);
    }
  }, [id, hydrate]);

  useEffect(() => { load(); }, [load]);

  // Whenever the session/status changes, keep contenteditable flag in sync.
  useEffect(() => {
    if (!editorRef.current || !session) return;
    editorRef.current.setAttribute("contenteditable", String(editable));
    // If the editor was mounted after session load, hydrate now.
    if (!hydratedRef.current && session.html) hydrate(session.html);
  }, [session, editable, hydrate]);

  // ---- Save ----
  const doSave = useCallback(async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch(`/documents/${id}/draft`, { html: currentHtml() });
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }, [dirty, id]);

  useEffect(() => {
    if (!dirty) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(doSave, 4000);
    return () => autosaveTimer.current && clearTimeout(autosaveTimer.current);
  }, [dirty, doSave]);

  const onEditorInput = () => { if (editable) setDirty(true); };

  // ---- Workflow actions ----
  const startEdit = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/edit/start`);
      setSession(data.session);
      hydratedRef.current = false;         // force fresh hydrate
      hydrate(data.session.html || "");
      toast.success("Edit session started");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const submitReview = async () => {
    await doSave();
    setSubmitting(true);
    try {
      await api.post(`/documents/${id}/submit-review`);
      toast.success("Submitted for review");
      hydratedRef.current = false;
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const reviewerDecision = async (action) => {
    setSubmitting(true);
    try {
      await api.post(`/documents/${id}/review-decision`, { action, comment: decisionComment });
      toast.success(action === "approve" ? "Forwarded to Approver" : "Sent back to Author");
      setDecisionComment("");
      hydratedRef.current = false;
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const approverDecision = async (action) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/documents/${id}/publish-decision`, { action, comment: decisionComment });
      toast.success(action === "approve" ? `Published v${data.version}` : "Rejected — returned to Author");
      setDecisionComment("");
      if (action === "approve") nav(`/documents/${id}`);
      else { hydratedRef.current = false; await load(); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const adminPublishNow = async () => {
    if (!window.confirm(
      "ADMIN BYPASS — Publish NOW?\n\n" +
      "This skips Reviewer + Approver and publishes the current draft directly as " +
      "the new controlled version (v" + ((doc?.current_version ?? 0) + 1) + "). " +
      "You will be stamped as both reviewer and approver on the audit log.\n\n" +
      "Continue?"
    )) return;
    if (dirty) await doSave();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/documents/${id}/admin-publish`, {
        action: "approve",
        comment: decisionComment || "Admin bypass — published without workflow",
      });
      toast.success(`Published v${data.version} via admin bypass`);
      setDecisionComment("");
      nav(`/documents/${id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const toggleRedline = async () => {
    if (redlineMode) { setRedlineMode(false); return; }
    try {
      // Save any pending edits first so redline compares latest text.
      if (dirty) await doSave();
      const { data } = await api.get(`/documents/${id}/redline`);
      setDiffHtml(data.diff_html || "");
      setBasePublishedHtml(data.base_html || "");
      setRedlineMode(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/documents/${id}/comments`, { text: newComment });
      setNewComment("");
      // Refresh comments without re-hydrating editor (would lose cursor).
      const { data } = await api.get(`/documents/${id}/draft`);
      setSession((prev) => ({ ...(prev || {}), comments: data.comments || [] }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const releaseLock = async () => {
    try {
      await api.post(`/documents/${id}/edit/release`);
      toast.success("Lock released");
      hydratedRef.current = false;
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const discardEdit = async () => {
    if (!window.confirm(
      "Discard ALL changes made in this edit session?\n\n" +
      "The document will revert to the currently published version (v" + (doc?.current_version ?? "?") + "). " +
      "This cannot be undone."
    )) return;
    try {
      await api.post(`/documents/${id}/edit/discard`);
      toast.success("Changes discarded — document reverted to published state");
      hydratedRef.current = false;
      setSession(null);
      setDirty(false);
      if (editorRef.current) editorRef.current.innerHTML = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // ---- Toolbar (execCommand) ----
  const exec = (cmd, val = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    setDirty(true);
  };
  const formatBlock = (tag) => exec("formatBlock", `<${tag}>`);

  const wf = useMemo(() => (session?.workflow || []).slice().reverse(), [session]);
  const comments = session?.comments || [];

  if (loading) return <div className="p-8 text-slate-500">Loading editor…</div>;
  if (!doc) return <div className="p-8 text-red-600">Document not found.</div>;
  const noSession = !session;

  return (
    <div className="space-y-4" data-testid="document-edit-page">
      <RLink to={`/documents/${id}`} className="label-caps hover:text-navy inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Back to document
      </RLink>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-navy uppercase">{doc.title}</h2>
          <div className="text-xs text-slate-500 font-mono mt-1">
            Editing from v{doc.current_version} (Published) · Source format preserved (logos, tables, fonts)
          </div>
        </div>
        {session && <Banner status={status} />}
      </div>

      {session?.lock && (
        <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 rounded-sm">
          <Lock size={14} />
          <span>
            Checked out by <b>{session.lock.user_name}</b> since{" "}
            <span className="font-mono">{new Date(session.lock.locked_at).toLocaleString()}</span>
          </span>
          {user?.role === "admin" && (
            <button onClick={releaseLock} data-testid="release-lock" className="ml-auto text-navy underline">Release lock</button>
          )}
        </div>
      )}

      {noSession && (
        <div className="card-flat p-8 text-center">
          <div className="text-sm text-slate-500 mb-3">This document has no active edit session.</div>
          {doc.status === "approved" ? (
            <button onClick={startEdit} data-testid="start-editing" className="btn-navy">Start Editing →</button>
          ) : (
            <div className="text-red-600 text-sm">Only Published documents can be edited (current: {doc.status}).</div>
          )}
        </div>
      )}

      {session && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          <div>
            {editable && (
              <div className="flex flex-wrap items-center gap-1.5 border border-slate-300 border-b-0 bg-slate-50 p-2 rounded-t-sm">
                <ToolbarBtn onClick={() => exec("bold")}><TextB size={14}/></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("italic")}><TextItalic size={14}/></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("underline")}><TextUnderline size={14}/></ToolbarBtn>
                <span className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarBtn onClick={() => formatBlock("h1")}><TextHOne size={14}/></ToolbarBtn>
                <ToolbarBtn onClick={() => formatBlock("h2")}><TextHTwo size={14}/></ToolbarBtn>
                <ToolbarBtn onClick={() => formatBlock("h3")}><TextHThree size={14}/></ToolbarBtn>
                <span className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarBtn onClick={() => exec("insertUnorderedList")}><ListBullets size={14}/></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("insertOrderedList")}><ListNumbers size={14}/></ToolbarBtn>
                <span className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarBtn onClick={() => exec("undo")}><ArrowUUpLeft size={14}/></ToolbarBtn>
                <span className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarBtn
                  active={redlineMode}
                  onClick={toggleRedline}
                  data-testid="toggle-redline"
                >
                  <GitDiff size={14}/> {redlineMode ? "Exit redline" : "Redline"}
                </ToolbarBtn>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-500">
                    {saving ? "Saving…" : dirty ? "Unsaved changes" : "All changes saved"}
                  </span>
                  <button
                    onClick={doSave}
                    disabled={!dirty || saving}
                    data-testid="save-draft"
                    className="btn-outline-navy text-xs px-2 py-1 disabled:opacity-40"
                  >
                    <FloppyDisk size={12} /> Save
                  </button>
                </div>
              </div>
            )}

            {(status === "in_review" || status === "in_approval") && (
              <div className="mb-2 text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm">
                <Warning size={12} className="inline mr-1 text-amber-600" />
                Read-only — awaiting {status === "in_review" ? "Reviewer" : "Approver"} decision.
              </div>
            )}

            {redlineMode ? (
              <div
                className="grid grid-cols-1 md:grid-cols-2 border border-slate-300 rounded-b-sm bg-white"
                data-testid="redline-inline"
              >
                <div className="p-4 border-r border-slate-200 max-h-[720px] overflow-auto">
                  <div className="label-caps mb-2 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
                    Published v{doc?.current_version}
                  </div>
                  <div className="lo-doc-container" dangerouslySetInnerHTML={{ __html: basePublishedHtml }} />
                </div>
                <div className="p-4 max-h-[720px] overflow-auto">
                  <div className="label-caps mb-2 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      Draft (with track changes)
                    </span>
                    <span className="text-[10px] font-mono text-emerald-700">+ additions</span>
                    <span className="text-[10px] font-mono text-red-700">− removals</span>
                  </div>
                  <div className="lo-doc-container" dangerouslySetInnerHTML={{ __html: diffHtml }} />
                </div>
              </div>
            ) : (
              <div
                ref={editorRef}
                contentEditable={editable}
                suppressContentEditableWarning
                onInput={onEditorInput}
                spellCheck={false}
                className="lo-doc-container editor-surface min-h-[600px] p-8 bg-white border border-slate-200 border-t-0 rounded-b-sm shadow-sm focus:outline-none"
                data-testid="editor-surface"
              />
            )}

            {(isAuthor || (canDiscard && !isAuthor)) && (status === "draft" || status === "rejected") && (
              <div className="mt-3 flex justify-between items-center gap-2">
                {canDiscard && (
                  <button
                    onClick={discardEdit}
                    disabled={submitting}
                    data-testid={isAuthor ? "discard-edit" : "discard-edit-admin"}
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold text-red-700 border border-red-300 bg-white rounded-sm hover:bg-red-50 disabled:opacity-40"
                    title={isAuthor ? "Discard your changes" : "Admin can discard any draft"}
                  >
                    <TrashSimple size={14} /> {isAuthor ? "Discard changes" : "Discard this draft (admin)"}
                  </button>
                )}
                {isAuthor && (
                  <button
                    onClick={submitReview}
                    disabled={submitting}
                    data-testid="submit-review"
                    className="btn-navy ml-auto"
                  >
                    <PaperPlaneTilt size={14} /> Submit for Review
                  </button>
                )}
              </div>
            )}
          </div>

          <aside className="space-y-3">
            <div className="card-flat p-4">
              <div className="label-caps mb-2">Workflow decisions</div>
              {user?.role === "admin" && (
                <div className="mb-3 p-2.5 border border-purple-300 bg-purple-50 rounded-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-purple-900 mb-1.5 flex items-center gap-1">
                    <Lightning size={12} weight="fill" /> Admin bypass
                  </div>
                  <textarea
                    value={decisionComment}
                    onChange={(e) => setDecisionComment(e.target.value)}
                    placeholder="Optional bypass reason (audited)"
                    rows={2}
                    data-testid="admin-bypass-comment"
                    className="w-full text-xs border border-purple-300 p-2 rounded-sm mb-2 bg-white"
                  />
                  <button
                    onClick={adminPublishNow}
                    disabled={submitting}
                    data-testid="admin-publish-now"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-sm disabled:opacity-40"
                    title="Skip reviewer + approver — publish this draft NOW as the new controlled version"
                  >
                    <Lightning size={14} weight="fill" /> Publish Now (skip workflow)
                  </button>
                  <div className="text-[10px] text-purple-900/70 mt-1.5 leading-tight">
                    Skips reviewer + approver. Stamps you as both on the audit log.
                  </div>
                </div>
              )}
              {status === "in_review" && isReviewer && !isAuthor && (
                <>
                  <textarea
                    value={decisionComment}
                    onChange={(e) => setDecisionComment(e.target.value)}
                    placeholder="Optional comment"
                    rows={3}
                    data-testid="reviewer-comment"
                    className="w-full text-xs border border-slate-300 p-2 rounded-sm mb-2"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => reviewerDecision("approve")} disabled={submitting}
                            data-testid="reviewer-approve" className="btn-navy text-xs flex-1">
                      <CheckCircle size={14} /> Forward to Approver
                    </button>
                    <button onClick={() => reviewerDecision("send_back")} disabled={submitting}
                            data-testid="reviewer-sendback" className="btn-outline-navy text-xs flex-1">
                      <ArrowUUpLeft size={14} /> Send back
                    </button>
                  </div>
                </>
              )}
              {status === "in_approval" && isApprover && (
                <>
                  <textarea
                    value={decisionComment}
                    onChange={(e) => setDecisionComment(e.target.value)}
                    placeholder="Optional comment"
                    rows={3}
                    data-testid="approver-comment"
                    className="w-full text-xs border border-slate-300 p-2 rounded-sm mb-2"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => approverDecision("approve")} disabled={submitting}
                            data-testid="approver-approve" className="btn-navy text-xs flex-1">
                      <CheckCircle size={14} /> Approve & Publish
                    </button>
                    <button onClick={() => approverDecision("reject")} disabled={submitting}
                            data-testid="approver-reject" className="btn-outline-navy text-xs flex-1">
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                </>
              )}
              {status === "in_review" && !isReviewer && (
                <div className="text-xs text-slate-500 italic">Waiting on reviewer.</div>
              )}
              {status === "in_approval" && !isApprover && (
                <div className="text-xs text-slate-500 italic">Waiting on approver.</div>
              )}
              {status === "in_review" && isAuthor && (
                <div className="text-xs text-slate-500 italic">You cannot review your own edit.</div>
              )}
              {(status === "draft" || status === "rejected") && (
                <div className="text-xs text-slate-500 italic">
                  Editing in progress. Submit when ready.
                </div>
              )}
            </div>

            <div className="card-flat p-4">
              <div className="label-caps mb-2 flex items-center gap-1"><ChatCircle size={12}/> Comments · {comments.length}</div>
              <div className="max-h-48 overflow-auto space-y-2 mb-2">
                {comments.length === 0 && <div className="text-xs italic text-slate-400">No comments yet.</div>}
                {comments.map((c) => (
                  <div key={c.id} className="text-xs border-l-2 border-cyan/40 pl-2">
                    <div className="text-[10px] font-mono text-slate-500">{c.author_name} · {new Date(c.at).toLocaleString()}</div>
                    <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
                  </div>
                ))}
              </div>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment for the Author…"
                rows={2}
                data-testid="new-comment"
                className="w-full text-xs border border-slate-300 p-2 rounded-sm"
              />
              <button onClick={addComment} data-testid="add-comment" className="btn-navy text-xs mt-1 w-full">
                Post comment
              </button>
            </div>

            <div className="card-flat p-4">
              <div className="label-caps mb-2">Workflow history</div>
              <div className="space-y-1.5 text-xs">
                {wf.length === 0 && <div className="italic text-slate-400">Not submitted yet.</div>}
                {wf.map((e, i) => (
                  <div key={i} className="border-l-2 border-slate-300 pl-2">
                    <div className="font-mono text-[10px] text-slate-500">
                      {new Date(e.at).toLocaleString()}
                    </div>
                    <div className="text-slate-800">
                      <b>{e.by_name}</b> — {e.action.replace(/_/g, " ")}
                    </div>
                    {e.comment && <div className="text-slate-600 italic">&ldquo;{e.comment}&rdquo;</div>}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
